import React, { useEffect, useState } from 'react';
import {
  Link, useNavigate, useParams 
} from 'react-router-dom';

import { VaultMessage } from '../../types/vault';

import { vaultClient } from './vault-client';

export function VaultView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ message, setMessage ] = useState<VaultMessage | null>(null);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const [ password, setPassword ] = useState('');
  const [ unlockError, setUnlockError ] = useState<string | null>(null);
  const [ isUnlocked, setIsUnlocked ] = useState(false);

  useEffect(() => {
    if (id) {
      loadMessage();
    }
  }, [ id ]);

  const loadMessage = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await vaultClient.find(id);
      if (data) {
        setMessage(data);
        // If message is not locked, it's automatically unlocked
        if (!data.isLocked) {
          setIsUnlocked(true);
        }
      } else {
        setError('Message not found');
      }
    } catch (err) {
      setError('Failed to load message');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !password) return;

    try {
      const success = await vaultClient.unlockMessage(id, password);
      if (success) {
        setIsUnlocked(true);
        setUnlockError(null);
      } else {
        setUnlockError('Incorrect password');
      }
    } catch (err) {
      setUnlockError('Failed to unlock message');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await vaultClient.destroy(id);
        navigate('/vault');
      } catch (err) {
        setError('Failed to delete message');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="vault-page">
        <div className="screen-container">
          <div className="screen-header">
            <div className="screen-header-back-button">
              <button className="back-button" onClick={() => navigate('/vault')}>
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            </div>
            <div className="screen-title">
              <h2>Loading Message...</h2>
            </div>
          </div>
          <div className="screen-content">
            <div className="loading-container">
              <p>Loading message...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="vault-page">
        <div className="screen-container">
          <div className="screen-header">
            <div className="screen-header-back-button">
              <button className="back-button" onClick={() => navigate('/vault')}>
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            </div>
            <div className="screen-title">
              <h2>Error</h2>
            </div>
          </div>
          <div className="screen-content">
            <div className="error-container">
              <p className="error-message">{error || 'Message not found'}</p>
              <button className="button" onClick={() => navigate('/vault')}>
                Back to Vault
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vault-page">
      <div className="screen-container">
        <div className="screen-header">
          <div className="screen-header-back-button">
            <button className="back-button" onClick={() => navigate('/vault')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          </div>
          <div className="screen-title">
            <h2>{message.title}</h2>
            <p>Created {formatDate(message.createdAt)}</p>
          </div>
        </div>

        <div className="screen-content">
          <div className="vault-message-detail">
            <div className="message-meta">
              <div className="message-recipients">
                <h3>Recipients:</h3>
                <div className="recipients-list">
                  {message.recipients.map((recipient) => (
                    <div key={recipient.id} className="recipient-item">
                      <span className="material-symbols-outlined">person</span>
                      <span>{recipient.firstName} {recipient.lastName}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="message-status">
                {message.isLocked ? (
                  <div className="lock-status locked">
                    <span className="material-symbols-outlined">lock</span>
                    <span>Locked Message</span>
                  </div>
                ) : (
                  <div className="lock-status unlocked">
                    <span className="material-symbols-outlined">lock_open</span>
                    <span>Unlocked Message</span>
                  </div>
                )}
              </div>
            </div>

            {message.isLocked && !isUnlocked ? (
              <div className="unlock-section">
                <div className="unlock-header">
                  <span className="material-symbols-outlined">lock</span>
                  <h3>This message is locked</h3>
                  <p>Enter the password to view the message content</p>
                </div>
                
                <form onSubmit={handleUnlock} className="unlock-form">
                  {unlockError && (
                    <div className="error-container">
                      <p className="error-message">{unlockError}</p>
                    </div>
                  )}
                  
                  <div className="input">
                    <label htmlFor="unlock-password">Password</label>
                    <input
                      id="unlock-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                    />
                  </div>
                  
                  <button type="submit" className="button">
                    <span className="material-symbols-outlined">lock_open</span>
                    Unlock Message
                  </button>
                </form>
              </div>
            ) : (
              <div className="message-content">
                <h3>Message Content</h3>
                <div className="content-display">
                  <p>{message.content}</p>
                </div>
              </div>
            )}

            <div className="message-actions">
              <Link to={`/vault/${message.id}/edit`} className="button secondary">
                <span className="material-symbols-outlined">edit</span>
                Edit Message
              </Link>
              <button 
                className="button secondary delete-button"
                onClick={handleDelete}
              >
                <span className="material-symbols-outlined">delete</span>
                Delete Message
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
